'use client';

import { useState } from 'react';

import { Check, Crown } from 'lucide-react';

import { Button } from '@/components/common/inputs/button/button';
import { Card } from '@/components/common/layout/card';
import { useToast } from '@/lib/hooks/useToast';
import { paymentsService } from '@/lib/services/payments.service';
import { useSession } from 'next-auth/react';
import { logger } from '@/lib/utils/logger';
import { useI18n } from '@/locales/client';

interface PricingFeature {
  name: string;
  included: boolean;
}

interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: PricingFeature[];
  isPopular?: boolean;
  isCurrent?: boolean;
  priceId: {
    monthly: string | null;
    yearly: string | null;
  };
}

const paymentsEnabled = false;

export default function PricingPlans({ onClose }: { onClose: () => void }) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    'monthly'
  );
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { data: session } = useSession();
  const t = useI18n();

  const plans: PricingPlan[] = [
    {
      name: t('upgrade.freemium.title'),
      description: t('upgrade.freemium.description'),
      monthlyPrice: 0,
      yearlyPrice: 0,
      priceId: {
        monthly: 'FREE',
        yearly: 'FREE',
      },
      isCurrent: true,
      features: [
        { name: t('upgrade.freemium.features.f1'), included: true },
        { name: t('upgrade.freemium.features.f2'), included: true },
        { name: t('upgrade.freemium.features.f3'), included: true },
      ],
    },
    {
      name: t('upgrade.premium.title'),
      description: t('upgrade.premium.description'),
      monthlyPrice: 30.0,
      yearlyPrice: 300.0,
      priceId: {
        monthly: 'PRO_MONTHLY',
        yearly: 'PRO_YEARLY',
      },
      isPopular: true,
      features: [
        { name: t('upgrade.premium.features.f1'), included: true },
        { name: t('upgrade.premium.features.f2'), included: true },
      ],
    },
  ];

  const handleSubscribe = async (plan: PricingPlan) => {
    try {
      // Return for now until we have a payment gateway
      if (!paymentsEnabled) {
        logger.info('Payment not enabled');
        return;
      } else {
        setIsLoading(true);
        const priceId = plan.priceId[billingCycle];

        if (!priceId) {
          toast({
            title: 'Configuration Error',
            description:
              'Subscription price not configured. Please contact support.',
            variant: 'destructive',
          });
          return;
        }

        if (!session) {
          toast({
            title: 'Authentication Required',
            description: 'Please sign in to subscribe to a plan.',
            variant: 'destructive',
          });
          return;
        }

        const response = await paymentsService.createCheckoutSession(
          priceId,
          session
        );

        if (response?.url) {
          window.location.href = response.url;
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: 'Failed to start subscription process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='relative mx-auto w-full p-4'>
      <div className='mb-2 text-center'>
        <h2 className='mb-4 text-3xl font-bold text-[#384250]'>
          {t('upgrade.title')}
        </h2>
        <div className='inline-block rounded-full bg-[#8659ff]/10 px-4 py-1.5 text-sm font-medium text-[#8659ff]'>
          {t('upgrade.pricingPlan')}
        </div>
        <div className='mt-4 flex min-h-24 flex-col items-center gap-2'>
          <div className='flex items-center gap-4'>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`rounded-full px-6 py-2.5 ${billingCycle === 'monthly' ? 'bg-[#8659ff] text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t('upgrade.monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`rounded-full px-6 py-2.5 ${billingCycle === 'yearly' ? 'bg-[#8659ff] text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t('upgrade.yearly')}
            </button>
          </div>
          {billingCycle === 'yearly' && (
            <div className='text-sm font-medium text-[#8659ff]'>
              {t('upgrade.discount')}
            </div>
          )}
        </div>
      </div>

      <div className='grid gap-4 md:grid-cols-2'>
        {plans.map((plan) => (
          <Card key={plan.name} className='relative p-6'>
            <div className='mb-4'>
              <h3
                className={`mb-2 text-2xl font-bold ${plan.isPopular ? 'text-[#8659ff]' : 'text-[#384250]'}`}
              >
                {plan.name}
              </h3>
              <p className='text-sm text-[#6c737f]'>{plan.description}</p>
            </div>

            <div className='mb-4 min-h-36'>
              <h4 className='mb-4 font-medium text-[#384250]'>
                {t('upgrade.features')}
              </h4>
              <ul className='space-y-3'>
                {plan.features.map((feature) => (
                  <li
                    key={feature.name}
                    className='flex items-center gap-3 text-sm text-[#6c737f]'
                  >
                    <Check className='h-4 w-4 text-[#8659ff]' />
                    {feature.name}
                  </li>
                ))}
              </ul>
            </div>

            <div className='mb-4'>
              <div className='flex items-end gap-1'>
                <span className='text-4xl font-bold'>
                  $
                  {billingCycle === 'monthly'
                    ? plan.monthlyPrice
                    : Math.floor(plan.yearlyPrice)}
                </span>
                <span className='mb-1 text-[#6c737f]'>
                  /
                  {billingCycle === 'monthly'
                    ? t('upgrade.month')
                    : t('upgrade.year')}
                </span>
              </div>
            </div>

            {plan.isCurrent ? (
              <Button
                disabled
                className='w-full cursor-not-allowed bg-gray-100 text-gray-500 hover:bg-gray-100'
              >
                {t('upgrade.current')}
              </Button>
            ) : (
              <Button
                className='w-full bg-[#8659ff] hover:bg-[#8659ff]/90'
                onClick={() => handleSubscribe(plan)}
                disabled={isLoading}
              >
                {isLoading ? (
                  'Processing...'
                ) : plan.isPopular ? (
                  <span className='flex items-center gap-2'>
                    {t('upgrade.goPro')}
                    <Crown className='h-4 w-4' />
                  </span>
                ) : (
                  'Select Plan'
                )}
              </Button>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
